import { useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Fade,
  Switch,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Block, Button, WebBackButton } from "../../components/shared";
import { getUserInfo } from "../../services/me";
import { GetUserInfoResponse } from "../../services/me/interface";
import {
  getP2pHistory,
  updateP2pSettings,
} from "../../services/personal";
import { P2pTransferDrawer } from "../../components/pages/p2p/P2pTransferDrawer";

/**
 * Экран P2P — переводы между пользователями внутри платформы.
 *
 * Составляющие:
 *   1. Тумблер «Разрешить принимать переводы» — управляет только тем,
 *      могут ли другие юзеры находить нас по email/phone.
 *   2. CTA «Перевести пользователю» — открывает 3-шаговый drawer с
 *      выбором получателя, суммы и валюты, вводом PIN.
 *   3. История переводов — in/out вперемешку, сортировка по времени.
 */
export function P2pPage() {
  const { t } = useTranslation(["p2p", "profile"]);
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: me } = useQuery<GetUserInfoResponse>(
    ["user-info"],
    getUserInfo,
    { staleTime: 30_000 },
  );

  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ["p2p-history"],
    queryFn: () => getP2pHistory(30),
    staleTime: 15_000,
  });

  const [transferOpen, setTransferOpen] = useState(false);

  const toggleMut = useMutation({
    mutationFn: (v: boolean) => updateP2pSettings(v),
    onMutate: (v) => {
      // Оптимистический апдейт — тумблер должен «летать».
      const prev = qc.getQueryData<GetUserInfoResponse>(["user-info"]);
      if (prev) {
        qc.setQueryData<GetUserInfoResponse>(["user-info"], {
          ...prev,
          allow_p2p_transfers: v,
        });
      }
      return { prev };
    },
    onError(err, _v, ctx) {
      if (ctx?.prev) qc.setQueryData(["user-info"], ctx.prev);
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
    onSettled() {
      qc.invalidateQueries(["user-info"]);
    },
  });

  const allowIncoming = me?.allow_p2p_transfers ?? true;

  return (
    <Fade in>
      <Box sx={{ width: "100%", marginBottom: "100px" }}>
        <WebBackButton
          to="/profile"
          title={t("p2p:title", { defaultValue: "Переводы пользователям" })}
        />

        {/* Тумблер «принимать» */}
        <Block sx={{ padding: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "16px 20px",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {t("p2p:allow_incoming", {
                  defaultValue: "Разрешить переводы",
                })}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  marginTop: "2px",
                }}
              >
                {t("p2p:allow_incoming_hint", {
                  defaultValue:
                    "Другие пользователи смогут переводить вам средства по вашему e-mail или телефону.",
                })}
              </Typography>
            </Box>
            <Switch
              checked={allowIncoming}
              disabled={toggleMut.isLoading}
              onChange={(e) => toggleMut.mutate(e.target.checked)}
            />
          </Box>
        </Block>

        {/* CTA */}
        <Button
          onClick={() => setTransferOpen(true)}
          sx={{ marginTop: "16px" }}
          startIcon={
            <Icon icon="solar:round-arrow-up-linear" width={22} height={22} />
          }
        >
          {t("p2p:make_transfer", {
            defaultValue: "Перевести пользователю",
          })}
        </Button>

        {/* История */}
        <Typography
          sx={{
            fontSize: "13px",
            color: "text.secondary",
            marginTop: "24px",
            marginBottom: "10px",
            paddingLeft: "6px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {t("p2p:history_title", { defaultValue: "История переводов" })}
        </Typography>

        <Block sx={{ padding: 0 }}>
          {historyLoading ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : history.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                {t("p2p:history_empty", {
                  defaultValue: "Переводов пока нет",
                })}
              </Typography>
            </Box>
          ) : (
            history.map((item, idx) => (
              <Box key={item.id}>
                <HistoryRow
                  direction={item.direction}
                  amount={item.amount}
                  currency={item.currency}
                  counterparty={item.counterparty.display_name}
                  createdAt={item.created_at}
                  comment={item.comment}
                />
                {idx < history.length - 1 && (
                  <Divider sx={{ mx: 2, opacity: 0.5 }} />
                )}
              </Box>
            ))
          )}
        </Block>

        <P2pTransferDrawer
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
        />
      </Box>
    </Fade>
  );
}

function HistoryRow({
  direction,
  amount,
  currency,
  counterparty,
  createdAt,
  comment,
}: {
  direction: "in" | "out";
  amount: string;
  currency: string;
  counterparty: string;
  createdAt: string;
  comment: string | null;
}) {
  const isIncoming = direction === "in";
  const dt = new Date(createdAt);
  const timeLabel = dt.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 20px",
      }}
    >
      <Box
        sx={(theme) => ({
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: isIncoming
            ? theme.palette.mode === "dark"
              ? "rgba(46, 204, 113, 0.15)"
              : "rgba(46, 204, 113, 0.12)"
            : theme.palette.mode === "dark"
              ? "rgba(255, 87, 87, 0.15)"
              : "rgba(255, 87, 87, 0.12)",
          color: isIncoming ? "#2ecc71" : "#e74c3c",
        })}
      >
        <Icon
          icon={
            isIncoming
              ? "solar:arrow-down-linear"
              : "solar:arrow-up-linear"
          }
          width={20}
          height={20}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {counterparty}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            marginTop: "2px",
          }}
        >
          {timeLabel}
          {comment ? ` · ${comment}` : ""}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 600,
          color: isIncoming ? "#2ecc71" : "text.primary",
          flexShrink: 0,
        }}
      >
        {isIncoming ? "+" : "−"}
        {amount} {currency}
      </Typography>
    </Box>
  );
}

function pickMessage(err: unknown): string {
  const raw =
    (err as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(", ") : raw;
  return msg || "Не удалось выполнить действие";
}
